import React, { useState, useEffect } from 'react';
import ProductList from './ProductList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTable, faTh, faGripHorizontal, faRectangleList, faThLarge, faChartLine, faChartArea, faChartBar, faArrowsAltV, faChartPie, faBuilding, faWallet, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  const options = { day: '2-digit', month: 'short', year: '2-digit' };
  return date.toLocaleDateString('en-GB', options);
};

class CategoryBlock extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      categoryData: {},
      categorySummary: {},
      childCategories: [],
      parentCategories: [],
      loading: true,
      error: null,
      blockData: {},
    };
  }

  componentDidMount() {
    this.fetchCategoryData();
  }

  fetchCategoryData() {
    const { config } = this.props;
    const apiUrl = `${config.PY_API_URL}category/${encodeURIComponent(config.categoryCode)}/children`;

    Promise.all([
      fetch(apiUrl).then(response => response.json()),
    ])
      .then(([categoryResponse]) => {
        const { category, child_category: childCategories, parent_categories: parentCategories, category_summary: categorySummary } = categoryResponse.data;
        this.setState({
          categoryData: category,
          childCategories,
          parentCategories,
          blockData: category.block_data,
          loading: false,
          categorySummary: categorySummary
        });
        document.title = `Money Marche - ${category.title}`;
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
          metaDescription.setAttribute('content', category.meta_description);
        }
        const metaTitle = document.querySelector('meta[name="title"]');
        if (metaTitle) {
          metaTitle.setAttribute('content', category.meta_title);
        }
        const metaKeyword = document.querySelector('meta[name="keywords"]');
        if (metaKeyword) {
          metaKeyword.setAttribute('content', category.meta_keywords);
        }      
      })
      .catch(error => {
        console.error('Error fetching category and block data:', error);
        this.setState({ loading: false, error: 'Error fetching data' });
      });
  }

  render() {
    const { categoryData, childCategories, parentCategories, loading, error, blockData, categorySummary } = this.state;
    const { config } = this.props;

    
    let hasInvestmentCategory = parentCategories.some(
        (parent) => (parent.category_code === "investments" || parent.category_code === "mutual-funds")
    );

    if (!hasInvestmentCategory) {
      if (categoryData.category_code === "investments" || categoryData.category_code === "mutual-funds") {
        hasInvestmentCategory = true;
      }
    }
    if (loading) {
      return <p>Loading...</p>;
    }

    if (error) {
      return <p>{error}</p>;
    }

    return (
      <div className="category-main">

        {config.showHeaderSection && (
        <div className="hero-section">
          <div className="hero-content">
            {config.showBreadcrumb && (
              <div className="hero-breadcrumb">
                <CustomCategoryBreadcrumb 
                  categories={parentCategories} 
                  currentCategory={categoryData} 
                  config={config} 
                />
              </div>
            )}
            <h1 className="hero-title">{categoryData.title || ''}</h1>
            <p className="hero-description">
              <span dangerouslySetInnerHTML={{ __html: categoryData.short_description || '' }} />
            </p>
            <a href="#" className="hero-cta">Start Your Journey Now</a>
          </div>

          {hasInvestmentCategory ? (
            <div className="hero-stats">
              <div className="stats-grid">
                <div className="stat-item highlight">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faChartPie} />
                  </div>
                  <div className="stat-label">Total Funds</div>
                  <div className="stat-value">{categorySummary?.total_funds || "N/A"}</div>
                </div>
                
                <div className="stat-item">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faBuilding} />
                  </div>
                  <div className="stat-label">Total AMC's</div>
                  <div className="stat-value">{categorySummary?.amc_count || "N/A"}</div>
                </div>
                
                <div className="stat-item">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faWallet} />
                  </div>
                  <div className="stat-label">Total AUM</div>
                  <div className="stat-value">{categorySummary?.total_aum || "N/A"}</div>
                </div>
                
                <div className="stat-item">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </div>
                  <div className="stat-label">Latest Launch</div>
                  <div className="stat-value">{formatDate(categorySummary?.latest_launch_date) || "N/A"}</div>
                </div>

                <div className="stat-item">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faChartLine} />
                  </div>
                  <div className="stat-label">1Y Returns</div>
                  <div className={`stat-value ${categorySummary?.returns_1yr > 0 ? 'positive' : 'negative'}`}>
                    {categorySummary?.returns_1yr?.toFixed(2) || "N/A"}%
                    {categorySummary?.returns_1yr > 0 ? 
                      <span className="stat-trend up">↑</span> : 
                      <span className="stat-trend down">↓</span>
                    }
                  </div>
                </div>

                <div className="stat-item">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faChartArea} />
                  </div>
                  <div className="stat-label">3Y Returns</div>
                  <div className={`stat-value ${categorySummary?.returns_3yr > 0 ? 'positive' : 'negative'}`}>
                    {categorySummary?.returns_3yr?.toFixed(2) || "N/A"}%
                    {categorySummary?.returns_3yr > 0 ? 
                      <span className="stat-trend up">↑</span> : 
                      <span className="stat-trend down">↓</span>
                    }
                  </div>
                </div>

                <div className="stat-item">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faChartBar} />
                  </div>
                  <div className="stat-label">5Y Returns</div>
                  <div className={`stat-value ${categorySummary?.returns_5yr > 0 ? 'positive' : 'negative'}`}>
                    {categorySummary?.returns_5yr?.toFixed(2) || "N/A"}%
                    {categorySummary?.returns_5yr > 0 ? 
                      <span className="stat-trend up">↑</span> : 
                      <span className="stat-trend down">↓</span>
                    }
                  </div>
                </div>

                <div className="stat-item">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faArrowsAltV} />
                  </div>
                  <div className="stat-label">3Y Range</div>
                  <div className="stat-value range">
                    <span className="negative">{categorySummary?.min_returns_3yr?.toFixed(2) || "N/A"}%</span>
                    <span className="separator">to</span>
                    <span className="positive">{categorySummary?.max_returns_3yr?.toFixed(2) || "N/A"}%</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="hero-stats">
              <div className="stats-grid">
                <div className="stat-item highlight">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faChartPie} />
                  </div>
                  <div className="stat-label">Total Products</div>
                  <div className="stat-value">{categorySummary?.total_funds || "N/A"}</div>
                </div>
                
                <div className="stat-item">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faBuilding} />
                  </div>
                  <div className="stat-label">Total AMC's</div>
                  <div className="stat-value">{categorySummary?.amc_count || "N/A"}</div>
                </div>
                
                <div className="stat-item">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </div>
                  <div className="stat-label">Latest Launch</div>
                  <div className="stat-value">{formatDate(categorySummary?.latest_launch_date) || "N/A"}</div>
                </div>
              </div>
            </div>
          )}
        </div>
        )}
        
        {!config.showHeaderSection && (config.showCategoryStats && hasInvestmentCategory ? (
            <div className="hero-stats">
              <div className="stats-grid">
                <div className="stat-item highlight">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faChartPie} />
                  </div>
                  <div className="stat-label">Total Funds</div>
                  <div className="stat-value">{categorySummary?.total_funds || "N/A"}</div>
                </div>
                
                <div className="stat-item">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faBuilding} />
                  </div>
                  <div className="stat-label">Total AMC's</div>
                  <div className="stat-value">{categorySummary?.amc_count || "N/A"}</div>
                </div>
                
                <div className="stat-item">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faWallet} />
                  </div>
                  <div className="stat-label">Total AUM</div>
                  <div className="stat-value">{categorySummary?.total_aum || "N/A"}</div>
                </div>
                
                <div className="stat-item">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </div>
                  <div className="stat-label">Latest Launch</div>
                  <div className="stat-value">{formatDate(categorySummary?.latest_launch_date) || "N/A"}</div>
                </div>

                <div className="stat-item">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faChartLine} />
                  </div>
                  <div className="stat-label">1Y Returns</div>
                  <div className={`stat-value ${categorySummary?.returns_1yr > 0 ? 'positive' : 'negative'}`}>
                    {categorySummary?.returns_1yr?.toFixed(2) || "N/A"}%
                    {categorySummary?.returns_1yr > 0 ? 
                      <span className="stat-trend up">↑</span> : 
                      <span className="stat-trend down">↓</span>
                    }
                  </div>
                </div>

                <div className="stat-item">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faChartArea} />
                  </div>
                  <div className="stat-label">3Y Returns</div>
                  <div className={`stat-value ${categorySummary?.returns_3yr > 0 ? 'positive' : 'negative'}`}>
                    {categorySummary?.returns_3yr?.toFixed(2) || "N/A"}%
                    {categorySummary?.returns_3yr > 0 ? 
                      <span className="stat-trend up">↑</span> : 
                      <span className="stat-trend down">↓</span>
                    }
                  </div>
                </div>

                <div className="stat-item">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faChartBar} />
                  </div>
                  <div className="stat-label">5Y Returns</div>
                  <div className={`stat-value ${categorySummary?.returns_5yr > 0 ? 'positive' : 'negative'}`}>
                    {categorySummary?.returns_5yr?.toFixed(2) || "N/A"}%
                    {categorySummary?.returns_5yr > 0 ? 
                      <span className="stat-trend up">↑</span> : 
                      <span className="stat-trend down">↓</span>
                    }
                  </div>
                </div>

                <div className="stat-item">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faArrowsAltV} />
                  </div>
                  <div className="stat-label">3Y Range</div>
                  <div className="stat-value range">
                    <span className="negative">{categorySummary?.min_returns_3yr?.toFixed(2) || "N/A"}%</span>
                    <span className="separator">to</span>
                    <span className="positive">{categorySummary?.max_returns_3yr?.toFixed(2) || "N/A"}%</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="hero-stats">
              <div className="stats-grid">
                <div className="stat-item highlight">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faChartPie} />
                  </div>
                  <div className="stat-label">Total Products</div>
                  <div className="stat-value">{categorySummary?.total_funds || "N/A"}</div>
                </div>
                
                <div className="stat-item">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faBuilding} />
                  </div>
                  <div className="stat-label">Total AMC's</div>
                  <div className="stat-value">{categorySummary?.amc_count || "N/A"}</div>
                </div>
                
                <div className="stat-item">
                  <div className="stat-icon">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </div>
                  <div className="stat-label">Latest Launch</div>
                  <div className="stat-value">{formatDate(categorySummary?.latest_launch_date) || "N/A"}</div>
                </div>
              </div>
            </div>
          ))}

        {config.sectionsOrder?.map((sectionKey) => {
          switch(sectionKey) {
            case 'childCategories':
              return config.showChildCategories && childCategories.length > 0 && (
                <div key="childCategories" className="equity-funds-section">
                  <div className="equity-header">
                    <h2>Types of {categoryData.name || 'Funds'}</h2>
                  </div>
                  
                  <div style={{overflow: 'scroll'}} className={`equity-funds-grid ${childCategories.length > 4 ? 'scrollable' : ''}`}>
                    {childCategories.map((category, index) => (
                      <>
                        
                      <div 
                        className="equity-fund-card"
                        style={{
                          width: childCategories.length <= 4 ? 
                            (childCategories.length === 3 ? 'calc(33.33% - 20px)' : 
                             childCategories.length === 2 ? 'calc(50% - 15px)' : 'calc(25% - 23px)') : '300px'
                        }}
                        key={index}
                      >
                        <div className="fund-icon">
                          <i className={`flaticonv3 ${category.icon_code || "flaticonv3-save-money"}`}></i>
                        </div>
                        <h3>{category.title}</h3>
                        <div className="fund-description">
                          <p dangerouslySetInnerHTML={{ __html: category.category_short_description || 'Description not available' }} />
                        </div>
                        <div className="item--button">
                          {category.custom_url && 
                            <a className="btn-arrow2" href={`${category.custom_url}`}>
                              <span>Read More</span>
                              <i className="flaticonv2-right-arrow"></i>
                            </a>
                          }
                          {!category.custom_url && 
                            <a className="btn-arrow2" href={`${config.siteUrl}/category/${category.category_code}`}>
                              <span>Read More</span>
                              <i className="flaticonv2-right-arrow"></i>
                            </a>
                          }
                        </div>
                      </div>
                      </>
                      
                    ))}
                  </div>
                </div>
              );

            case 'productList':
              return config.showProductList && (
                <div key="productList">
                  <ProductList config={config} hasInvestmentCategory={hasInvestmentCategory} />
                </div>
              );

            case 'description':
              return config.showDescription && categoryData.long_description && (
                <div key="description" style={{ clear: 'both' }}>
                  <p dangerouslySetInnerHTML={{ __html: categoryData.long_description || '' }} />
                </div>
              );

            case 'whyNecessary':
              return config.showWhyNecessary && categoryData.why_necessary_content && (
                <div 
                  key="whyNecessary"
                  className="why-necessary-section"
                  dangerouslySetInnerHTML={{ __html: categoryData.why_necessary_content }}
                />
              );
            case 'whyUs':
              return config.showWhyUs && categoryData.why_us_content && (
                <div 
                  key="whyUs"
                  className="why-us-section"
                  dangerouslySetInnerHTML={{ __html: categoryData.why_us_content }}
                />
              );
            case 'investmentSpread':
              return config.showInvestmentSpread && categoryData.investment_spread_content && (
                <div 
                  key="investmentSpread"
                  className="investment-spread-section"
                  dangerouslySetInnerHTML={{ __html: categoryData.investment_spread_content }}
                />
              );
            case 'expertManagers':
              return config.showExpertManagers && categoryData.expert_managers_content && (
                <div 
                  key="expertManagers"
                  className="expert-managers-section"
                  dangerouslySetInnerHTML={{ __html: categoryData.expert_managers_content }}
                />
              );
            case 'connect':
              return config.showConnect && categoryData.connect_content && (
                <div 
                  key="connect"
                  className="connect-section"
                  dangerouslySetInnerHTML={{ __html: categoryData.connect_content }}
                />
              );
            case 'benefits':
              return config.showBenefits && categoryData.benefits_content && (
                <div 
                  key="benefits"
                  className="benefits-section"
                  dangerouslySetInnerHTML={{ __html: categoryData.benefits_content }}
                />
              );
            default:
              return null;
          }
        })}
      </div>
    );
  }
}

const CustomCategoryBreadcrumb = ({ categories, currentCategory, config }) => {
  const breadcrumbs = [
    <a key="home" href={config.siteUrl}>Home</a>
  ];

  if (categories && categories.length > 0) {
    categories.forEach((category, index) => {
      breadcrumbs.push(
        <span key={category.category_code}>
          {' » '}
          {category.custom_url && 
            <a href={`${category.custom_url}`}>
              {category.category_name || category.name || ""}
            </a>
          }
          {!category.custom_url && 
            <a href={`${config.siteUrl}/category/${category.category_code}`}>
              {category.name}
            </a>
          }
        </span>
      );
    });
  }

  if (currentCategory) {
    breadcrumbs.push(
      <span key={currentCategory.category_code}>
        {' » '}
        {currentCategory.name}
      </span>
    );
  }

  return (
    <div className="custom-breadcrumb">
      {breadcrumbs}
    </div>
  );
};


export default CategoryBlock;
